import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CreditCard, CheckCircle, AlertCircle } from 'lucide-react';
import { getSafePublishableKey } from '../../config/stripe';
import { usePaymentMethod } from '../../hooks/Billing/usePaymentMethod';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux';
import { SavePaymentMethodRequest } from '../../types/billing';

// Safely initialize Stripe
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let stripePromise: Promise<any> | null = null;
try {
  const safePublishableKey = getSafePublishableKey();
  if (safePublishableKey && safePublishableKey.startsWith('pk_')) {
    stripePromise = loadStripe(safePublishableKey);
  } else {
    console.error('Invalid Stripe publishable key');
  }
} catch (error) {
  console.error('Error initializing Stripe:', error);
}

interface AddPaymentMethodModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (paymentMethodId: string) => void;
  userId?: string;
  customerId?: string;
  setAsDefault?: boolean;
}

// Inner component that uses Stripe hooks
const AddPaymentMethodForm: React.FC<{ 
  onSuccess: (paymentMethodId: string) => void; 
  onClose: () => void;
  userId?: string;
  customerId?: string;
  setAsDefault?: boolean;
}> = ({ onSuccess, onClose, userId, customerId, setAsDefault }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [succeeded, setSucceeded] = useState(false);
  
  // Get practice data from Redux
  const { selectedPractice } = useSelector((state: RootState) => state.practices);
  
  // Get userId from localStorage as fallback if not provided as prop
  const getUserId = () => {
    if (userId) {
      return userId;
    }
    // Fallback: try to get from localStorage
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const fallbackUserId = user?.["sub"] || user?.sub || user?.userId || user?.id;
      if (fallbackUserId) {
        return fallbackUserId;
      }
    } catch (error) {
      console.error('Error getting userId from localStorage:', error);
    }
    return undefined;
  };
  
  // Get the final userId to use
  const finalUserId = getUserId();
  
  // Log props on mount to debug
  useEffect(() => {
    
    // Warn if userId is missing
    if (!finalUserId) {
      console.warn('⚠️ WARNING: userId is missing! Payment method cannot be saved without userId.', {
        userId,
        finalUserId
      });
    }
  }, [userId, finalUserId, customerId, setAsDefault]);
  
  // Get customer ID from user or practice data if available
  const getCustomerId = () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      
      // Check user object first
      const userCustomerId = 
        user?.stripe_customer_id || 
        user?.customerId || 
        user?.stripeCustomerId ||
        user?.attributes?.stripe_customer_id ||
        user?.attributes?.customerId;
      
      // Only use user customer ID (no practiceId anymore)
      const customerId = userCustomerId;
      
      if (customerId) {
      } else {
        console.warn('⚠️ No customer ID found. User keys:', Object.keys(user));
      }
      
      return customerId || undefined;
    } catch (error) {
      console.error('❌ Error getting customer ID:', error);
      return undefined;
    }
  };

  // Use the hook to save payment method through Redux slice
  const { save: savePaymentMethodToDB, isSaving, error: saveError } = usePaymentMethod({
    userId: finalUserId, // Use finalUserId which includes fallback from localStorage
    customerId: customerId || getCustomerId(), // Use prop customerId if provided, otherwise get from localStorage
  });

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      // Get the card element
      const cardElement = elements.getElement(CardElement);
      
      if (!cardElement) {
        throw new Error('Card element not found');
      }

      // Get customer ID before creating payment method
      const finalCustomerId = customerId || getCustomerId();

      // Create payment method directly using Stripe Elements
      // Note: Stripe's API doesn't accept custom fields like userId - that goes to our backend
      const { error: createError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });

      if (createError) {
        throw new Error(createError.message || 'Failed to create payment method');
      }

      if (paymentMethod) {
        
        // Attach payment method to customer if we have a customerId
        // This is important for Stripe to associate the payment method with the customer
        if (finalCustomerId) {
          try {
            // Note: Attaching requires backend call since we need secret key
            // We'll handle this in the backend when saving
          } catch (attachError) {
            console.warn('⚠️ Could not attach payment method to customer (will be handled by backend):', attachError);
          }
        }
        
        // Save payment method to MongoDB via Redux slice (hook -> slice -> service)
        // Only use userId (sub from localStorage)
        
        // Only save if we have userId (required)
        if (finalUserId) {
          try {
            // Build saveRequest object - only include userId (no practiceId)
            const saveRequest: SavePaymentMethodRequest = {
              paymentMethodId: paymentMethod.id,
              isDefault: setAsDefault || false,
              userId: finalUserId, // Always include userId (sub from localStorage)
            };
            
            // Include customerId if available
            if (finalCustomerId) {
              saveRequest.customerId = finalCustomerId;
            }
            
            
            // Double-check userId is actually in the object
            if (!('userId' in saveRequest)) {
              console.error('❌ CRITICAL: userId is NOT in saveRequest object!');
              console.error('❌ saveRequest keys:', Object.keys(saveRequest));
            }
            const savedPaymentMethod = await savePaymentMethodToDB(saveRequest);

          } catch (saveError) {
            console.error('❌ Error saving payment method to database:', saveError);
            // The error is already handled by the Redux slice
            throw saveError; // Re-throw to show error in UI
          }
        } else {
          throw new Error('userId is required to save payment method. Please ensure you are logged in.');
        }

        setSucceeded(true);
        setTimeout(() => {
          onSuccess(paymentMethod.id);
          onClose();
        }, 1500);
      } else {
        throw new Error('No payment method returned from Stripe');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to add payment method';
      setError(errorMessage);
      console.error('Error adding payment method:', err);
    } finally {
      setIsProcessing(false);
    }
  };

  if (succeeded) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center py-8"
      >
        <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
          <CheckCircle className="w-8 h-8 text-green-600" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Payment Method Added!</h3>
        <p className="text-gray-600">Your payment method has been saved successfully.</p>
      </motion.div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Card Information
        </label>
        <div className="border border-gray-300 rounded-lg p-4 bg-white">
          <CardElement
            options={{
              style: {
                base: {
                  fontSize: '16px',
                  color: '#424770',
                  '::placeholder': {
                    color: '#aab7c4',
                  },
                  fontFamily: 'system-ui, -apple-system, sans-serif',
                },
                invalid: {
                  color: '#9e2146',
                },
              },
            }}
          />
        </div>
      </div>

      {(error || saveError) && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-3 p-4 bg-red-50 rounded-xl border border-red-200"
        >
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <p className="text-red-600 text-sm">{error || saveError}</p>
        </motion.div>
      )}

      <div className="flex gap-3">
        <button
          type="button"
          onClick={onClose}
          disabled={isProcessing}
          className="flex-1 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!stripe || isProcessing || isSaving}
          className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
        >
          {isProcessing || isSaving ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              <span>Saving...</span>
            </>
          ) : (
            <>
              <CreditCard className="w-4 h-4" />
              <span>Add Payment Method</span>
            </>
          )}
        </button>
      </div>

      <div className="flex items-center justify-center space-x-2 text-xs text-gray-500">
        <CreditCard className="w-3 h-3" />
        <span>Your payment information is secure and encrypted</span>
      </div>
    </form>
  );
};

export const AddPaymentMethodModal: React.FC<AddPaymentMethodModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  userId,
  customerId,
  setAsDefault = false,
}) => {
  if (!isOpen) return null;

  if (!stripePromise) {
    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 p-4 bg-red-50 rounded-xl border border-red-200">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
              <div>
                <p className="text-red-800 font-medium">Stripe Not Configured</p>
                <p className="text-red-600 text-sm">Please configure your Stripe publishable key.</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="mt-4 w-full px-4 py-2 bg-gray-600 text-white rounded-xl hover:bg-gray-700 transition-colors"
            >
              Close
            </button>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    );
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          className="bg-white rounded-3xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Add Payment Method</h2>
                <p className="text-sm text-gray-500">Enter your card details</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
            >
              <X className="w-4 h-4 text-gray-600" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            <Elements stripe={stripePromise}>
              <AddPaymentMethodForm 
                onSuccess={onSuccess} 
                onClose={onClose}
                userId={userId}
                customerId={customerId}
                setAsDefault={setAsDefault}
              />
            </Elements>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
